import { useQuery } from '@tanstack/react-query'
import { processStepsApi, type ProcessAssignableUser } from '@/api/processStepsApi'
import { useMockApi } from '@/lib/useMock'
import { useTenant } from '@/providers/TenantProvider'
import { processAssignableUsersKey } from '@/lib/processStepKeys'
import { useProcessRunBatchReady } from '@/hooks/useProcessRunBatch'

/** Mock roster so the Assign To picker stays populated offline. */
const MOCK_USERS: ProcessAssignableUser[] = [
  { id: 'mock-paul', name: 'Paul P.' },
  { id: 'mock-aaron', name: 'Aaron A.' },
  { id: 'mock-maria', name: 'Maria L.' },
  { id: 'mock-devon', name: 'Devon S.' },
]

/**
 * Roster for the process-step Assign To picker.
 *
 * Its own hook rather than a reuse of `useAssignableUsers` (equipment) or
 * `useProcedureAssignableUsers`: this picker must offer exactly the set the
 * process assign endpoint accepts — any enabled platform user — and must open
 * to everyone holding `process.execute`.
 *
 * Borrowing the equipment roster was the bug this replaces. That route filters
 * to members of the selected tenant and needs `equipment.read`, so it returned
 * an empty picker to every superadmin (who needs no membership row) and to
 * every role without equipment access — silently, because a 403 reads as an
 * empty list here.
 *
 * Pass `enabled: false` where the caller lacks `process.execute`; the route
 * 403s them, so the query must never fire.
 */
export function useProcessAssignableUsers(
  enabled: boolean,
  /**
   * Process field id of the step being assigned. When given, the roster is the
   * people QUALIFIED for that step rather than everyone who could be handed
   * one. Part of the query key, so two steps on the same form do not share a
   * cached roster.
   */
  fieldId?: string,
): {
  users: ProcessAssignableUser[]
  isLoading: boolean
} {
  const { selectedTenantId } = useTenant()
  // Inside a run form, wait for its batch read, which fills this key (z8nrz7e8kw).
  const batchReady = useProcessRunBatchReady()

  const query = useQuery({
    queryKey: processAssignableUsersKey(selectedTenantId, fieldId),
    enabled: enabled && !useMockApi && selectedTenantId !== null && batchReady,
    queryFn: () => processStepsApi.listAssignableUsers(selectedTenantId as string, fieldId),
  })

  return {
    users: useMockApi ? MOCK_USERS : (query.data ?? []),
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
  }
}
